// HYK-1109: shared gating for auto-opening the Contribute sheet after a genuine new subscribe.
// Callers keep owning their own `showContribution` state/render — this hook only decides
// whether to open it (evaluate) and tracks the per-creator dismissal cooldown (handleClose).
import { useCallback, useEffect, useRef } from 'react';
import { wasContributePromptDismissedRecently, markContributePromptDismissed, DISABLE_AUTO_CONTRIBUTE_PROMPT } from '../../helpers/contributePromptGuard';

export function useAutoContributePrompt(subscriberId, currentCreatorId, isOpen) {
  const targetIdRef = useRef(null);
  // Bumped on every sheet close, every cancel(), and whenever the page's displayed creator
  // changes. Lets evaluate() detect that its pending attempt is stale.
  const generationRef = useRef(0);
  // Mirrors the caller's current sheet-open state on every render, so evaluate() can read it
  // live at async resolution time instead of a value captured (and potentially stale by then)
  // at click time.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    generationRef.current += 1;
    // Only clear a tracked target if there's no sheet currently open for it — otherwise a
    // legitimately auto-opened sheet that's still showing when the user navigates away would
    // lose its dismissal tracking, and its eventual close would never arm the cooldown.
    if (!isOpenRef.current) {
      targetIdRef.current = null;
    }
  }, [currentCreatorId]);

  /** Call right before dispatching the subscribe thunk; pass the result to evaluate(). */
  const beginSubscribeAttempt = useCallback(() => generationRef.current, []);

  /** Invalidates any pending attempt (e.g. the user unsubscribed) without touching the dismissal cooldown. */
  const cancel = useCallback(() => {
    generationRef.current += 1;
    targetIdRef.current = null;
  }, []);

  /** Call from the subscribe thunk's resolution. Returns true if the sheet should be showing. */
  const evaluate = useCallback((creatorId, { success, eligible = true, startedAtGeneration } = {}) => {
    if (
      DISABLE_AUTO_CONTRIBUTE_PROMPT ||
      !success ||
      !eligible ||
      startedAtGeneration !== generationRef.current ||
      wasContributePromptDismissedRecently(subscriberId, creatorId)
    ) {
      return false;
    }
    // Only claim ownership of the open (and therefore its eventual dismissal) if this call is
    // what's opening the sheet — not if it's already open for an unrelated reason (manual
    // "Contribute" button, deep link, resume-contribution event). isOpenRef is read live, not
    // captured at click time, so this is correct even if the sheet was opened by something
    // else while this subscribe request was still in flight.
    if (!isOpenRef.current) {
      targetIdRef.current = creatorId;
    }
    return true;
  }, [subscriberId]);

  /** Call from the sheet's onClose. Marks the dismissal only if this open was auto-triggered. */
  const handleClose = useCallback(() => {
    generationRef.current += 1;
    if (targetIdRef.current) {
      markContributePromptDismissed(subscriberId, targetIdRef.current);
      targetIdRef.current = null;
    }
  }, [subscriberId]);

  return { beginSubscribeAttempt, evaluate, handleClose, cancel };
}
